import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, Variants } from 'framer-motion'
import { useTokenPrice } from '~/hooks/useTokenPrice'
import { useLimitSwapPair, useLimitOrderUserConfig } from '~/state/dex/hooks'
import BigNumber from 'bignumber.js'
import { Container } from './styled'

const MarketRateRow: React.FC = () => {
  const { t } = useTranslation()
  const [inputToken, outputToken] = useLimitSwapPair()
  const inputPrice = useTokenPrice(inputToken?.address)
  const outputPrice = useTokenPrice(outputToken?.address)
  const { isInputRateDirection } = useLimitOrderUserConfig()

  const marketRateLiteral = useMemo(() => {
    if (!Number(inputPrice) || !Number(outputPrice)) return '~'
    const rate = new BigNumber(inputPrice).div(outputPrice)
    const calcScale = +rate < 1e-6 ? 12 : 6
    return rate.decimalPlaces(calcScale).toFixed()
  }, [inputPrice, outputPrice])

  const outputMarketRateLiteral = useMemo(() => {
    if (!Number(inputPrice) || !Number(outputPrice)) return '~'
    const rate = new BigNumber(outputPrice).div(inputPrice)
    const calcScale = +rate < 1e-6 ? 12 : 6
    return rate.decimalPlaces(calcScale).toFixed()
  }, [inputPrice, outputPrice])

  const leftToken = isInputRateDirection ? inputToken : outputToken
  const rightToken = isInputRateDirection ? outputToken : inputToken
  const rightTokenPrice = useTokenPrice(rightToken?.address)

  const displayRateLiteral = useMemo(() => {
    return isInputRateDirection ? marketRateLiteral : outputMarketRateLiteral
  }, [isInputRateDirection, marketRateLiteral, outputMarketRateLiteral])

  const displayRateUSDLiteral = useMemo(() => {
    const usd = new BigNumber(displayRateLiteral)
      .multipliedBy(rightTokenPrice)
      .toFixed(2)
    return Number(usd) ? `(~$${usd})` : '(~)'
  }, [displayRateLiteral, rightTokenPrice])

  return (
    <Container>
      <span>{`${t('market_rate')} 1 ${leftToken?.symbol} =`}</span>
      <motion.span
        key={displayRateLiteral}
        variants={variants}
        animate={'show'}
        initial="hide"
      >
        &nbsp;{displayRateLiteral}&nbsp;
      </motion.span>
      <span>{rightToken?.symbol}</span>
      &nbsp;
      <motion.span
        key={displayRateUSDLiteral}
        variants={variants}
        animate={'show'}
        initial="hide"
      >
        {displayRateUSDLiteral}
      </motion.span>
    </Container>
  )
}

export default MarketRateRow

const variants: Variants = {
  show: {
    opacity: 1,
    y: 0,
    transition: {
      ease: 'easeOut',
      duration: 0.5,
    },
  },
  hide: {
    y: -20,
    opacity: 0,
  },
}
